import {
  addDoc,
  deleteDoc,
  deleteField,
  doc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import {
  db,
  groupsCollectionRef,
  rotationsCollectionRef,
} from '../firebase';
import {
  AuditActor,
  asFirestoreData,
  logActivity,
  stripSentinels,
} from '../audit-log';
import { diffFields } from '../diff-fields';
import type { Rotation } from '../types';

const rotationLabel = (r: { name: string }): string => r.name;

export const createRotation = async (
  actor: AuditActor | null,
  payload: Record<string, unknown>,
): Promise<string> => {
  const docRef = await addDoc(rotationsCollectionRef, payload);
  const snapshot = stripSentinels(payload);
  logActivity({
    actor,
    entity: 'rotation',
    entityRef: docRef.id,
    entityLabel: rotationLabel(snapshot as { name: string }),
    action: 'create',
    eventRef: snapshot.eventRef as string | undefined,
    snapshot,
  });
  return docRef.id;
};

export const updateRotation = async (
  actor: AuditActor | null,
  before: Rotation,
  payload: Record<string, unknown>,
): Promise<void> => {
  await updateDoc(
    doc(rotationsCollectionRef, before.ref),
    asFirestoreData(payload),
  );
  const changes = diffFields(before, payload);
  if (Object.keys(changes).length === 0) return;
  logActivity({
    actor,
    entity: 'rotation',
    entityRef: before.ref,
    entityLabel: rotationLabel(before),
    action: 'update',
    eventRef: before.eventRef,
    changes,
  });
};

export const deleteRotation = async (
  actor: AuditActor | null,
  before: Rotation,
): Promise<void> => {
  await deleteDoc(doc(rotationsCollectionRef, before.ref));
  const { ref, ...rest } = before;
  logActivity({
    actor,
    entity: 'rotation',
    entityRef: before.ref,
    entityLabel: rotationLabel(before),
    action: 'delete',
    eventRef: before.eventRef,
    snapshot: rest as unknown as Record<string, unknown>,
  });
};

// Clears `rotationRef` on member groups then deletes the rotation in one
// batch so member groups never end up pointing at a missing rotation doc.
export const deleteRotationWithGroupCleanup = async (
  actor: AuditActor | null,
  rotation: Rotation,
  memberGroupRefs: string[],
): Promise<void> => {
  const batch = writeBatch(db);
  memberGroupRefs.forEach((groupRef) => {
    batch.update(doc(groupsCollectionRef, groupRef), {
      rotationRef: deleteField(),
    });
  });
  batch.delete(doc(rotationsCollectionRef, rotation.ref));
  await batch.commit();
  const { ref, ...rest } = rotation;
  logActivity({
    actor,
    entity: 'rotation',
    entityRef: rotation.ref,
    entityLabel: rotationLabel(rotation),
    action: 'delete',
    eventRef: rotation.eventRef,
    snapshot: rest as unknown as Record<string, unknown>,
    meta: {
      clearedRotationOnGroupCount: memberGroupRefs.length,
    },
  });
};
